/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/
import { SVGIcons } from '../../map/svg';
import { VisionApplication } from '../../application';
import { TimeSelect } from '../../components/timeselect';
import { VisionLayout } from '../../layout';
import { CameraDTO, CameraService } from '../avl_tab/camera_service';
import { CameraImageWindow } from '../avl_tab/camera_window';
import { VisionGroupList } from '../components/group_list';
import { VisionCameraMap } from './map';

/** Defines a collection of camera images for a specific asset.
 *
 */
class AssetPictures {
    markers: google.maps.marker.AdvancedMarkerElement[];
    images: CameraDTO[];

    constructor() {
        this.markers = new Array<google.maps.marker.AdvancedMarkerElement>();
        this.images = new Array<CameraDTO>();
    }
}

/** Defines the camera tab in the application.
 *
 */
export class VisionCameraTab {
    app: VisionApplication;
    service!: CameraService;

    cameraCache: Map<string, CameraDTO>;
    cameraMarkerCache: Map<string, google.maps.marker.AdvancedMarkerElement>;

    assetMarkerCache: Map<string, AssetPictures>;

    layout!: dhtmlXLayoutObject;
    paramForm!: dhtmlXForm;
    assetGrid!: dhtmlXGridObject;
    toolbar!: dhtmlXToolbarObject;

    cameraMap!: VisionCameraMap;
    initComplete: boolean;

    count: number;
    cameraWindowOpen: boolean;
    cameraWindow!: CameraImageWindow;

    constructor(vision: VisionLayout) {
        this.app = VisionApplication.getInstance();
        this.initComplete = false;
        this.count = 0;
        this.cameraMarkerCache = new Map<string, google.maps.marker.AdvancedMarkerElement>();
        this.cameraCache = new Map<string, CameraDTO>();
        this.assetMarkerCache = new Map<string, AssetPictures>();
        this.cameraWindowOpen = false;

        const config: IdhtmlXLayoutConfig = {
            parent: '',
            pattern: '3J',
        };
        this.layout = vision.tabbar.cells('CAMERA').attachLayout(config);
    }

    public init(): void {
        if (this.initComplete == false) {
            this.service = this.app.cameraService;

            this.initComplete = true;
            this.setupUI();
            this.cameraMap = new VisionCameraMap(this);
        } else {
            this.app.setMapWorkspace(this.cameraMap.map.map);
        }
    }

    public afterMapInit() {
        this.populateGrid();
        this.setupEvents();
        this.app.setMapWorkspace(this.cameraMap.map.map);
    }

    private setupUI(): void {
        const formData = JSON.parse(JSON.stringify(TimeSelect.SHORT_FORM));
        formData.push({ type: 'button', value: 'Search', name: 'search' });

        this.paramForm = this.layout.cells('a').attachForm(formData);
        this.paramForm.getInput(TimeSelect.START).autocomplete = 'off';
        this.paramForm.getInput(TimeSelect.END).autocomplete = 'off';

        this.layout.cells('a').hideHeader();
        this.layout.cells('a').fixSize(false, true);
        this.layout.cells('a').setHeight(200);
        this.layout.cells('a').setWidth(400);

        this.assetGrid = this.layout.cells('c').attachGrid();
        this.layout.cells('c').hideHeader();
        this.assetGrid.setHeader('Name,Last Image,View', '', ['text-align:left;', 'text-align:center;', 'text-align:center;']);
        this.assetGrid.setColTypes('ro,ro,ch');
        this.assetGrid.init();

        this.toolbar = this.layout.cells('c').attachToolbar();
        //const groupList = new VisionGroupList(this.toolbar, this.assetGrid, 'asset_folder_camera', this.filter);
        new VisionGroupList(this.toolbar, this.assetGrid, 'asset_folder_camera', this.filter);

        this.layout.cells('b').hideHeader();

        this.assetGrid.attachEvent('onRowSelect', (id) => {
            this.assetSelected(id);
        });
    }

    private setupEvents(): void {
        this.paramForm.attachEvent('onButtonClick', (id) => {
            switch (id) {
                case 'search':
                    this.getData();
                    break;
            }
        });

        // Fired when user changes the form values.
        this.paramForm.attachEvent('onChange', (name, value, _state) => {
            // User picks from the time selection window.
            if (name == TimeSelect.SELECT) {
                const select = new TimeSelect(this.paramForm);
                select.selectRange(value as string);
            }
        });

        this.assetGrid.attachEvent('onCheckbox', (rid, _cid, state) => {
            this.toggleAssetEvent(rid, state);
        });
    }

    /** Event fired when user clicks the checkbox in the asset grid.
     *  Should toggle the camera marker images on the map on/off
     * @param assetId The asset that was picked
     * @param state The state of the checkbox to turn markers on/off
     */
    private toggleAssetEvent(assetId: string, state: boolean) {
        const collection = this.assetMarkerCache.get(assetId);
        if (collection) {
            collection.markers.forEach((marker) => {
                if (state == true) {
                    marker.map = this.cameraMap.map.map;
                } else {
                    marker.map = null;
                }
            });
        }
    }

    private filter(id: string) {
        const folder = VisionApplication.getInstance().assetFolderService.folderCache?.get(id);

        this.assetGrid?.forEachRow((rowId) => {
            this.assetGrid.setRowHidden(rowId, true);
        });

        this.assetGrid?.forEachRow((rowId) => {
            this.assetGrid.setRowHidden(rowId, true);
            if (folder?.items.some((e) => e.id === rowId)) {
                this.assetGrid?.setRowHidden(rowId, false);
            }
            if (folder?.items.some((e) => e.id === this.assetGrid.getUserData(rowId, 'parent'))) {
                this.assetGrid?.setRowHidden(rowId, false);
            }
        });
    }

    private populateGrid(): void {
        this.assetGrid.clearAll(false);
        this.count = 0;

        this.app.assetService.getAssetMap().then((assetMap) => {
            assetMap.forEach((asset) => {
                this.assetGrid.addRow(asset.id.id, [asset.id.name, '-', 1], 0, 0, '', false);
                this.count = this.count + 1;
            });
        });

        /*
        this.app.assetService.assetList.forEach((asset) => {
            this.assetGrid.addRow(asset.id.id, [asset.id.name, '-', 1], 0, 0, '', false);
            this.count = this.count + 1;
        });
        */
    }

    private clearCaches() {
        this.cameraCache.clear();
        this.cameraMarkerCache.forEach((item) => {
            item.map = null;
        });
        this.cameraCache.clear();
        this.assetMarkerCache.clear();
    }

    private getData(): void {
        this.clearCaches();

        const startTime = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const endTime = this.paramForm.getItemValue(TimeSelect.END).getTime();

        const request = this.service.getInDateRange(startTime, endTime);
        const infoWindow = new google.maps.InfoWindow({
            content: "Hello, I'm a popup!",
            pixelOffset: new google.maps.Size(8, -32),
        });

        request.then((data) => {
            if (data.parsedBody) {
                data.parsedBody.forEach((item) => {
                    const iowaDate = new Intl.DateTimeFormat('en-US', {
                        timeZone: 'America/Chicago',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                    }).format(new Date(item.timestamp));

                    const date = new Date(item.timestamp);

                    const formatter = new Intl.DateTimeFormat('en-US', {
                        timeZone: 'America/Chicago',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                    });
                    const parts = formatter.formatToParts(date);
                    const lookup: Record<string, string> = {};
                    for (const part of parts) {
                        if (part.type !== 'literal') {
                            lookup[part.type] = part.value;
                        }
                    }

                    const iowaTime = `${lookup.month}/${lookup.day}/${lookup.year} ${lookup.hour}:${lookup.minute}:${lookup.second}`;

                    /*
                    const marker = new google.maps.Marker({
                        map: this.cameraMap.map.map,
                        position: new google.maps.LatLng(item.latitude, item.longitude),
                        icon: {
                            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(SVGIcons.camera('black')),
                            scaledSize: new google.maps.Size(32, 32), // Resize the SVG if needed
                        },
                    });
                    */
                    /*
                    const contentDiv = document.createElement('div');
                    contentDiv.style.width = '48px';
                    contentDiv.style.height = '48px';
                    contentDiv.innerHTML = SVGIcons.camera('black'); // your SVG string

                    // Create the marker
                    const asset = this.app.assetService.assetList.get(item.asset);
                    const marker = new google.maps.marker.AdvancedMarkerElement({
                        map: this.cameraMap.map.map,
                        position: { lat: item.latitude, lng: item.longitude },
                        title: asset?.id.name + '\n' + iowaTime,
                        content: contentDiv, // <-- must be a Node
                    });
                    */

                    const asset = this.app.assetService.assetList.get(item.asset);
                    const name = asset?.id.name ?? 'N/A';

                    const marker = this.createLabeledMarker(this.cameraMap.map.map, { lat: item.latitude, lng: item.longitude }, SVGIcons.camera('black'), name);

                    this.assetGrid.cells(item.asset, 1).setValue(iowaDate);

                    marker.addListener('click', () => {
                        this.cameraMarkerClicked(item.id, item.asset);
                    });

                    /*
                    marker.addListener('mouseover', () => {
                        infoWindow.setPosition(marker.position);
                        infoWindow.open(this.cameraMap.map);

                        const asset = this.app.assetService.assetList.get(item.asset);
                        if (asset) {
                            infoWindow.setContent(asset.id.name + '<br>' + iowaTime);
                        }
                    });
                    */
                    // Hide popup when mouse leaves
                    //marker.addListener('mouseout', () => {
                    //    infoWindow.close();
                    //});

                    this.cameraCache.set(item.id, item);
                    this.cameraMarkerCache.set(item.id, marker);

                    let collection = this.assetMarkerCache.get(item.asset);
                    if (collection == null) {
                        collection = new AssetPictures();
                        this.assetMarkerCache.set(item.asset, collection);
                    }
                    collection.markers.push(marker);
                    collection.images.push(item);
                });
            }
        });
    }

    private cameraMarkerClicked(id: string, assetId: string) {
        if (this.cameraWindowOpen == false) {
            console.log('Camera Marker Was Clicked: ' + id);
            this.cameraWindow = new CameraImageWindow(this.app.windowManager, this);
            this.cameraWindowOpen = true;
        }

        if (this.cameraWindow) {
            this.cameraWindow.loadImage(id, assetId);
        }
    }

    private assetSelected(assetId: string): void {
        const assetMarkers = this.assetMarkerCache.get(assetId);
        const first = assetMarkers?.images.at(0)?.id;
        if (first != undefined) {
            if (this.cameraWindowOpen == false) {
                this.cameraWindow = new CameraImageWindow(this.app.windowManager, this);
                this.cameraWindowOpen = true;
            }

            if (this.cameraWindow) {
                this.cameraWindow.loadImage(first, assetId);
            }
        }
    }

    createLabeledMarker(map: google.maps.Map, position: google.maps.LatLngLiteral, svgString: string, labelText: string): google.maps.marker.AdvancedMarkerElement {
        // Wrapper div
        const markerContent = document.createElement('div');
        markerContent.style.position = 'relative';
        markerContent.style.display = 'flex';
        markerContent.style.flexDirection = 'column';
        markerContent.style.alignItems = 'center';

        // ---------- Label ----------
        const label = document.createElement('div');
        label.innerText = labelText;
        label.style.background = 'white';
        label.style.color = 'black';
        label.style.fontSize = '12px';
        label.style.fontWeight = 'bold';
        label.style.padding = '2px 6px';
        label.style.borderRadius = '4px';
        label.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
        label.style.position = 'absolute';
        label.style.bottom = '100%';
        label.style.marginBottom = '4px';

        // Pointer triangle
        const pointer = document.createElement('div');
        pointer.style.width = '0';
        pointer.style.height = '0';
        pointer.style.borderLeft = '6px solid transparent';
        pointer.style.borderRight = '6px solid transparent';
        pointer.style.borderTop = '6px solid white';
        pointer.style.position = 'absolute';
        pointer.style.bottom = '100%';
        pointer.style.marginBottom = '0px';

        markerContent.appendChild(label);
        markerContent.appendChild(pointer);

        // ---------- SVG Marker ----------
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
        const svgElement = svgDoc.documentElement;
        svgElement.setAttribute('width', '32');
        svgElement.setAttribute('height', '32');

        markerContent.appendChild(svgElement);

        // ---------- Create AdvancedMarkerElement ----------
        const marker = new google.maps.marker.AdvancedMarkerElement({
            map,
            position,
            content: markerContent,
        });

        return marker;
    }

    updateMarkerSvg(marker: google.maps.marker.AdvancedMarkerElement, newSvgString: string) {
        const content = marker.content as HTMLElement | null;
        if (!content) return;

        // Find the existing SVG element
        const oldSvg = content.querySelector('svg');
        if (!oldSvg) return;

        // Parse and create new SVG element
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(newSvgString, 'image/svg+xml');
        const newSvg = svgDoc.documentElement;
        newSvg.setAttribute('width', oldSvg.getAttribute('width') ?? '32');
        newSvg.setAttribute('height', oldSvg.getAttribute('height') ?? '32');

        // Replace the SVG node
        oldSvg.replaceWith(newSvg);
    }
}
